/**
 * OfflineIndicator - Ícone de status na navbar com função de sincronização
 */

import { useState } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { Wifi, WifiOff, Cloud, CloudOff, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SyncStatus } from '@/services/syncService';
import { toast } from 'sonner';

export default function OfflineIndicator() {
  const { isOnline, syncStatus, localStats, triggerSync, refreshLocalStats } = useOffline();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleClick = async () => {
    if (!isOnline) {
      toast.error('Sem conexão com a internet');
      return;
    }
    
    if (syncStatus.status === SyncStatus.SYNCING || isSyncing) {
      toast.info('Sincronização em andamento...');
      return;
    }

    if (localStats.sync_queue_size === 0) {
      toast.success('Tudo sincronizado!');
      return;
    }

    setIsSyncing(true);
    try {
      toast.loading('Sincronizando...', { id: 'indicator-sync' });
      const result = await triggerSync();
      
      if (result.success) {
        toast.success('Sincronizado!', { id: 'indicator-sync' });
      } else {
        toast.error('Erro', { id: 'indicator-sync', description: result.message });
      }
    } catch (error) {
      toast.error('Erro ao sincronizar', { id: 'indicator-sync' });
    } finally {
      setIsSyncing(false);
      await refreshLocalStats();
    }
  };

  // Definir ícone e cor baseado no estado
  const getStatusConfig = () => {
    // Sincronizando
    if (syncStatus.status === SyncStatus.SYNCING || isSyncing) {
      return {
        icon: RefreshCw,
        color: 'text-blue-500',
        bgColor: 'bg-blue-100 hover:bg-blue-200',
        label: 'Sincronizando...',
        sublabel: `${syncStatus.progress}/${syncStatus.total}`,
        animate: true,
        cursor: 'cursor-wait'
      };
    }

    // Erro de sync
    if (syncStatus.status === SyncStatus.ERROR) {
      return {
        icon: AlertCircle,
        color: 'text-red-500',
        bgColor: 'bg-red-100 hover:bg-red-200',
        label: 'Erro na sincronização',
        sublabel: 'Clique para tentar novamente',
        animate: false,
        cursor: 'cursor-pointer'
      };
    }

    // Offline
    if (!isOnline) {
      return {
        icon: WifiOff,
        color: 'text-amber-500',
        bgColor: 'bg-amber-100',
        label: 'Sem conexão',
        sublabel: localStats.sync_queue_size > 0 
          ? `${localStats.sync_queue_size} pendentes` 
          : 'Modo offline ativo',
        animate: false,
        cursor: 'cursor-not-allowed'
      };
    }

    // Online com itens pendentes
    if (localStats.sync_queue_size > 0) {
      return {
        icon: Cloud,
        color: 'text-amber-600',
        bgColor: 'bg-amber-100 hover:bg-amber-200',
        label: `${localStats.sync_queue_size} pendente${localStats.sync_queue_size > 1 ? 's' : ''}`,
        sublabel: 'Clique para sincronizar',
        animate: false,
        cursor: 'cursor-pointer'
      };
    }

    // Online e sincronizado
    return {
      icon: Check,
      color: 'text-green-500',
      bgColor: 'bg-green-100 hover:bg-green-200',
      label: 'Sincronizado',
      sublabel: syncStatus.lastSync 
        ? `Última: ${new Date(syncStatus.lastSync).toLocaleTimeString('pt-BR')}`
        : '',
      animate: false,
      cursor: 'cursor-pointer'
    };
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className={`relative p-2 rounded-full transition-all ${config.bgColor} ${config.cursor}`}
            disabled={!isOnline && localStats.sync_queue_size === 0}
            data-testid="offline-indicator"
          >
            <Icon 
              className={`w-4 h-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`} 
            />
            
            {/* Badge de contagem */}
            {localStats.sync_queue_size > 0 && !config.animate && (
              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {localStats.sync_queue_size > 99 ? '99+' : localStats.sync_queue_size}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="text-sm">
            <p className="font-medium">{config.label}</p>
            {config.sublabel && (
              <p className="text-xs text-gray-500 mt-0.5">{config.sublabel}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Versão compacta para mobile
export function OfflineIndicatorCompact() {
  const { isOnline, syncStatus, localStats } = useOffline();

  if (syncStatus.status === SyncStatus.SYNCING) {
    return (
      <div className="flex items-center gap-1 text-blue-500">
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span className="text-xs">Sync</span>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="flex items-center gap-1 text-amber-500">
        <WifiOff className="w-3 h-3" />
        <span className="text-xs">Offline</span>
      </div>
    );
  }

  if (localStats.sync_queue_size > 0) {
    return (
      <div className="flex items-center gap-1 text-amber-500">
        <Cloud className="w-3 h-3" />
        <span className="text-xs">{localStats.sync_queue_size}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-green-500">
      <Check className="w-3 h-3" />
    </div>
  );
}
